/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.25.0
 */

import { FC } from 'react';

import { Formik } from 'formik';
import { useSelector } from 'react-redux';

import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSettings,
} from '@/main/modules/internationalization/intSelectors';
import { KitLoader } from '@/main/modules/kitLoader';
import {
    useGetNotificationsSettingsQuery,
    useUpdateNotificationsSettingsMutation,
} from '@/main/modules/userNotifications/store';
import ErrorPage from '@/main/pages/ErrorPage/ErrorPage';
import { makeSelectAuthoritiesIsAssistant } from '@/main/pages/PageAuth/selectors/authSelectors';
import { icons } from '@/main/theme';
import { FormSubmitHandler } from '@/main/types/globalTypes';

import { PageSettingsContentStyled, PageSettingsHeaderStyled } from '../../styled/PageSettingsStyled';
import { useFormDefaultValues } from '../hooks/useFormDefaultValues';
import { useStatusModals } from '../hooks/useStatusModals';
import { TNotificationsSettingsForm } from '../types';
import { getNotificationsSettingsValidationSchema } from '../validations';
import SettingsNotificationsForm from './SettingsNotificationsForm';


const SettingsNotifications: FC = () => {
    const settingsTranslation = useSelector(makeSelectTranslationPageSettings());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const isAssistant = useSelector(makeSelectAuthoritiesIsAssistant());

    const {
        data: settingsData,
        isFetching: settingsIsFetching,
        isError: settingsIsError,
    } = useGetNotificationsSettingsQuery();
    const [
        updateSettings,
        { isLoading: updateSettingsIsLoading, isError: updateSettingsIsError, isSuccess: updateSettingsIsSuccess },
    ] = useUpdateNotificationsSettingsMutation();

    const defaultFormValues = useFormDefaultValues();

    const validationSchema = getNotificationsSettingsValidationSchema(isAssistant);

    useStatusModals(updateSettingsIsLoading, updateSettingsIsSuccess, updateSettingsIsError);

    const handleSubmit: FormSubmitHandler<TNotificationsSettingsForm> = (data, { setSubmitting, resetForm }) => {
        updateSettings(data)
            .unwrap()
            .catch(() => {
                setSubmitting(false);
                resetForm();
            });
    };

    const settingsIsLoading = settingsIsFetching || updateSettingsIsLoading;

    const initialFormValues = settingsData ?? defaultFormValues;

    if (settingsIsError && !settingsIsFetching) {
        return (
            <ErrorPage
                src={icons.errorDeliveryImage3x}
                text={translationCommon.error.ui.text}
                title={translationCommon.error.ui.title}
            />
        );
    }

    return (
        <PageSettingsContentStyled>
            <PageSettingsHeaderStyled>{settingsTranslation.settingsNotifications.title}</PageSettingsHeaderStyled>
            <KitLoader showLoader={settingsIsLoading}>
                <Formik<TNotificationsSettingsForm>
                    enableReinitialize
                    initialValues={initialFormValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    <SettingsNotificationsForm defaultFormValues={defaultFormValues} />
                </Formik>
            </KitLoader>
        </PageSettingsContentStyled>
    );
};

export default SettingsNotifications;
